<div class="completion-reports">
    <app-page-data-panel
        title="Execution Completion Reports"
        subtitle="Issue immutable, versioned PDFs across delivery schedules in this POSM execution.">

        @if (message()) {
            <div class="report-message success"><i class="pi pi-check-circle"></i>{{ message() }}</div>
        }
        @if (error()) {
            <div class="report-message error"><i class="pi pi-exclamation-circle"></i>{{ error() }}</div>
        }
        @if (generating()) {
            <div class="report-message warning" role="status"><i class="pi pi-spin pi-spinner"></i>{{ generationJob()?.status === 'QUEUED' ? 'Report queued.' : 'Preparing the PDF and its images.' }} You can leave this page and return later.</div>
        }
        @if (latestReport(); as latest) {
            <div class="latest-report">
                <div><strong>Latest PDF: {{ latest.reportNumber || 'Completion report' }}</strong>
                    <span>Version {{ latest.version || 1 }} &middot; {{ latest.generatedAt | date:'medium' }}</span></div>
                <p-button label="Download latest PDF" icon="pi pi-download" [outlined]="true"
                    [loading]="reportActions.isPending(downloadActionKey(latest))" (onClick)="download(latest)"></p-button>
            </div>
        }

        @if (summary(); as data) {
            <div class="report-summary">
                <div class="summary-card"><strong>{{ data.totalScheduleCount || 0 }}</strong><span>All delivery schedules</span></div>
                <div class="summary-card success"><strong>{{ data.completedScheduleCount || 0 }}</strong><span>Completed schedules</span></div>
                <div class="summary-card warn"><strong>{{ data.incompleteScheduleCount || 0 }}</strong><span>Incomplete schedules</span></div>
                <div class="summary-card"><strong>{{ includeIncompleteSchedules ? (data.totalScheduleCount || 0) : (data.completedScheduleCount || 0) }}</strong><span>Schedules in next PDF</span></div>
            </div>

            <div class="evidence-counts">
                <span><strong>{{ data.approvedCompletionEvidenceCount || 0 }}</strong> approved schedule images</span>
                <span><strong>{{ data.approvedSignedDeliveryNoteCount || 0 }}</strong> signed delivery notes</span>
                <span><strong>{{ data.approvedSupportingDocumentCount || 0 }}</strong> supporting documents</span>
                <span><strong>{{ data.pendingDeliveryEvidenceCount || 0 }}</strong> schedule assets awaiting review</span>
                <span><strong>{{ data.uniqueSourceFileCount || 0 }}</strong> distinct uploads / {{ data.evidenceAssociationCount || 0 }} schedule assets</span>
            </div>

            <div class="print-option">
                <p-checkbox [(ngModel)]="includeIncompleteSchedules" [binary]="true" inputId="includeIncompleteSchedules"></p-checkbox>
                <label for="includeIncompleteSchedules">
                    <strong>Include incomplete delivery schedules</strong>
                    <span>When off, the generated version contains only schedules at the DELIVERED stage.</span>
                </label>
            </div>
            @if (generationReason()) {
                <div class="report-message warning"><i class="pi pi-info-circle"></i>{{ generationReason() }}</div>
            }
        } @else if (loading()) {
            <div class="empty-state"><i class="pi pi-spin pi-spinner"></i><span>Loading execution delivery status and report versions.</span></div>
        }
    </app-page-data-panel>

    <app-page-data-panel title="Delivery Report Asset Review" subtitle="Review each location and delivery schedule independently. Shared uploads are referenced under every applicable schedule.">
        @for (location of locationGroups(); track location.key) {
            <section class="location-group">
                <button type="button" class="group-heading" (click)="toggleLocation(location.key)" [attr.aria-expanded]="expandedLocations().has(location.key)">
                    <i [class]="expandedLocations().has(location.key) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
                    <strong>{{ location.name }}</strong>
                    <span>{{ location.schedules.length }} schedules &middot; {{ location.assetCount }} schedule assets</span>
                </button>
                @if (expandedLocations().has(location.key)) {
                    @for (schedule of location.schedules; track schedule.key) {
                        <div class="schedule-group">
                            <button type="button" class="schedule-heading" (click)="toggleSchedule(schedule.key)" [attr.aria-expanded]="expandedSchedules().has(schedule.key)">
                                <i [class]="expandedSchedules().has(schedule.key) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
                                <strong>{{ schedule.number }}</strong>
                                <span class="schedule-details">
                                    <span>Item: {{ schedule.itemName || '—' }}</span>
                                    <span>Qty: {{ schedule.standQty ?? '—' }}</span>
                                    <span>Size: {{ schedule.standSize || '—' }}</span>
                                </span>
                                <span class="schedule-asset-count">{{ schedule.assets.length }} assets</span>
                            </button>
                            @if (expandedSchedules().has(schedule.key)) {
                                <ng-container *ngTemplateOutlet="assetTable; context: { $implicit: schedule.assets, editable: true }"></ng-container>
                            }
                        </div>
                    }
                }
            </section>
        }
        @if (unmappedEvidence().length) {
            <section class="unmapped-group">
                <h3><i class="pi pi-exclamation-triangle"></i> Files needing a schedule link</h3>
                <p>These files cannot be approved until their stop or payload is linked to a delivery schedule.</p>
                <ng-container *ngTemplateOutlet="assetTable; context: { $implicit: unmappedEvidence(), editable: false }"></ng-container>
            </section>
        }
        @if (!deliveryEvidence().length && !loading()) {
            <div class="empty-state"><i class="pi pi-file"></i><span>No files are available from linked shipment trips.</span></div>
        }
    </app-page-data-panel>

    <ng-template #assetTable let-assets let-editable="editable">
        <p-table [value]="assets" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '64rem' }" [paginator]="assets.length > 10" [rows]="10">
            <ng-template pTemplate="header">
                <tr><th>File</th><th>Trip / scope</th><th>Uploaded</th><th>Report asset type</th><th>Review</th><th class="text-right">Action</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-evidence>
                <tr>
                    <td>
                        @if (evidence.fileUrl && isImageAsset(evidence) && !unavailableImages().has(evidenceKey(evidence))) {
                            <p-image [src]="evidence.fileUrl" [alt]="(evidence.fileName || 'Evidence') + ' / ' + (evidence.locationName || '') + ' / ' + (evidence.scheduleNumber || '')"
                                [preview]="true" loading="lazy" width="72" height="52"
                                [imageStyle]="{ 'object-fit': 'cover', 'border-radius': '.35rem' }" imageClass="evidence-thumb" (onImageError)="imageUnavailable(evidence)"></p-image>
                        } @else {
                            <span class="evidence-file-icon"><i [class]="isImageAsset(evidence) ? 'pi pi-image' : 'pi pi-file'"></i></span>
                            @if (isImageAsset(evidence)) { <span class="block text-sm text-600">Image unavailable</span> }
                        }
                        @if (evidence.fileUrl) {
                            <a class="asset-file-name" [href]="evidence.fileUrl" target="_blank" rel="noopener noreferrer">{{ evidence.fileName || 'Open file' }} <i class="pi pi-external-link"></i></a>
                        } @else { <strong class="block">{{ evidence.fileName || 'Evidence file' }}</strong> }
                        <span class="block text-sm text-600">{{ evidence.fileType || 'Document' }}</span>
                    </td>
                    <td>
                        <p-tag [value]="evidence.scope || 'DELIVERY'" severity="info"></p-tag>
                        <span class="block text-sm text-600">{{ tripTypeLabel(evidence.tripType) }}</span>
                        <span class="block text-sm text-600">{{ evidence.shipmentNumber || ('Shipment #' + evidence.shipmentId) }}</span>
                        @if (evidence.deliveryId) { <span class="block text-sm text-600">{{ evidence.deliveryNumber || ('Delivery #' + evidence.deliveryId) }}</span> }
                        @if (evidence.stopId) { <span class="block text-sm text-600">Stop #{{ evidence.stopId }}</span> }
                    </td>
                    <td>{{ evidence.uploadedAt | date:'medium' }}</td>
                    <td>
                        @if (editable && (evidence.approvalStatus || 'PENDING') === 'PENDING') {
                            <p-select [options]="reportAssetTypeOptions" [(ngModel)]="evidence.reportAssetType" optionLabel="label" optionValue="value"
                                placeholder="Select type" appendTo="body" [style]="{ 'min-width': '13rem' }"></p-select>
                        } @else { <span>{{ reportAssetTypeLabel(evidence.reportAssetType) }}</span> }
                    </td>
                    <td>
                        @if (editable) {
                            <p-tag [value]="evidence.approvalStatus || 'PENDING'" [severity]="evidenceSeverity(evidence.approvalStatus)"></p-tag>
                            @if (evidence.reviewedByActor || evidence.reviewedByCustomerId) {
                                <span class="block review-actor">{{ reviewedBy(evidence) }}</span>
                                <span class="block text-sm text-600">{{ evidence.reviewedAt ? (evidence.reviewedAt | date:'medium') : '-' }}</span>
                            }
                            @if (evidence.reviewComment) { <span class="block review-comment">{{ evidence.reviewComment }}</span> }
                            @if (evidence.migratedSharedReview) { <span class="block text-sm text-600">Migrated shared review</span> }
                        } @else {
                            <p-tag value="Needs mapping" severity="warn"></p-tag>
                            <span class="block text-sm text-600">{{ evidence.mappingIssue }}</span>
                        }
                    </td>
                    <td class="text-right">
                        @if (editable) {
                            <div class="review-actions">
                                @if ((evidence.approvalStatus || 'PENDING') === 'PENDING') {
                                    <p-button label="Approve" icon="pi pi-check" size="small" [loading]="reviewingEvidenceKey() === evidenceKey(evidence)" (onClick)="reviewEvidence(evidence, true)"></p-button>
                                    <p-button label="Reject" icon="pi pi-times" severity="danger" [outlined]="true" size="small" [disabled]="reviewingEvidenceKey() === evidenceKey(evidence)" (onClick)="reviewEvidence(evidence, false)"></p-button>
                                }
                                <p-button [label]="(evidence.approvalStatus || 'PENDING') === 'PENDING' ? 'Review details' : 'Edit review'" icon="pi pi-pencil" severity="secondary" [outlined]="true" size="small" (onClick)="editReview(evidence)"></p-button>
                                <p-button label="History" icon="pi pi-history" severity="secondary" [text]="true" size="small" (onClick)="showHistory(evidence)"></p-button>
                            </div>
                        }
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </ng-template>

    <p-dialog header="Edit schedule asset review" [(visible)]="reviewDialogVisible" [modal]="true" appendTo="body" [style]="{ width: '38rem', 'max-width': '95vw' }">
        @if (reviewDraft; as draft) {
            <div class="review-dialog-context"><strong>{{ draft.evidence.locationName }}</strong><span>{{ draft.evidence.scheduleNumber }}</span><span>{{ draft.evidence.fileName }}</span></div>
            <div class="review-latest">Latest decision: {{ draft.evidence.approvalStatus || 'PENDING' }} &middot; {{ reviewedBy(draft.evidence) }}
                @if (draft.evidence.reviewedAt) { <span> &middot; {{ draft.evidence.reviewedAt | date:'medium' }}</span> }
            </div>
            @if (reviewError()) { <div class="report-message warning">{{ reviewError() }}</div> }
            <label class="review-field">Decision
                <p-select [(ngModel)]="draft.approved" [options]="[{ label: 'Approve', value: true }, { label: 'Reject', value: false }]" optionLabel="label" optionValue="value" appendTo="body"></p-select>
            </label>
            <label class="review-field">Report asset type
                <p-select [(ngModel)]="draft.reportAssetType" [options]="reportAssetTypeOptions" optionLabel="label" optionValue="value" placeholder="Select type" appendTo="body"></p-select>
            </label>
            <label class="review-field">Comment (optional)<textarea pTextarea [(ngModel)]="draft.comment" rows="4" maxlength="2000"></textarea></label>
            <div class="review-dialog-actions">
                @if (staleReview()) { <p-button label="Load latest review" icon="pi pi-refresh" severity="secondary" (onClick)="reloadReview()"></p-button> }
                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="reviewDialogVisible = false"></p-button>
                <p-button label="Save review" icon="pi pi-check" [disabled]="staleReview()" [loading]="reviewingEvidenceKey() === evidenceKey(draft.evidence)" (onClick)="saveReview()"></p-button>
            </div>
        }
    </p-dialog>
    <p-dialog header="Review history" [(visible)]="historyDialogVisible" [modal]="true" appendTo="body" [style]="{ width: '45rem', 'max-width': '95vw' }">
        @if (historyAsset(); as asset) { <div class="review-dialog-context"><strong>{{ asset.locationName }}</strong><span>{{ asset.scheduleNumber }} &middot; {{ asset.fileName }}</span></div> }
        @if (historyLoading()) { <p>Loading review history...</p> }
        @if (historyError()) { <div class="report-message error">{{ historyError() }}</div> }
        @for (entry of reviewHistory(); track entry.reviewRevision) {
            <div class="review-history-entry">
                <p-tag [value]="entry.approvalStatus || 'PENDING'" [severity]="evidenceSeverity(entry.approvalStatus)"></p-tag>
                <strong>{{ reviewedBy(entry) }}</strong><span>{{ entry.reviewedAt | date:'medium' }}</span>
                <span>{{ reportAssetTypeLabel(entry.reportAssetType) }}</span>
                <span class="text-sm text-600">Revision {{ entry.reviewRevision }} Â· {{ entry.action === 'REVIEW_EDIT' ? 'Review edited' : 'Review decision' }}</span>
                @if (entry.action === 'MIGRATED_SHARED_REVIEW') { <span class="text-sm text-600">Migrated shared review &middot; recorded {{ entry.recordedAt | date:'medium' }}</span> }
                @if (entry.reviewComment) { <p>{{ entry.reviewComment }}</p> }
            </div>
        }
        @if (!historyLoading() && !historyError() && !reviewHistory().length) { <p>No review decisions have been recorded for this schedule asset.</p> }
    </p-dialog>

    <app-page-data-panel title="Version History" subtitle="Every issued PDF remains an immutable execution snapshot.">
        @if (reports().length) {
            <p-table [value]="reports()" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '54rem' }">
                <ng-template pTemplate="header">
                    <tr><th>Version</th><th>Scope</th><th>Schedules</th><th>Generated</th><th>Generated by</th><th>Status</th><th class="text-right">PDF</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-report>
                    <tr>
                        <td><strong>{{ report.reportNumber || 'Completion report' }}</strong><br/><span class="text-sm text-600">Version {{ report.version || 1 }}</span></td>
                        <td>{{ report.includeIncompleteSchedules ? 'Completed + incomplete' : 'Completed only' }}</td>
                        <td>{{ report.includedScheduleCount || 0 }} included / {{ report.omittedScheduleCount || 0 }} omitted</td>
                        <td>{{ report.generatedAt | date:'medium' }}</td>
                        <td>{{ generatedBy(report) }}</td>
                        <td><p-tag [value]="report.status || 'GENERATED'" [severity]="statusSeverity(report.status)"></p-tag></td>
                        <td class="text-right"><p-button label="Download" icon="pi pi-download" severity="secondary" [outlined]="true" size="small" [loading]="reportActions.isPending(downloadActionKey(report))" (onClick)="download(report)"></p-button></td>
                    </tr>
                </ng-template>
            </p-table>
        } @else {
            <div class="empty-state"><i class="pi pi-file-pdf"></i><span>No execution completion report has been issued yet.</span></div>
        }
    </app-page-data-panel>
</div>
